'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ANCHOR_IDS,
  AnchorClusterKey,
  CLUSTER_IDS,
  ID_TO_CLUSTER,
  SCROLL_MARGIN_TOP,
} from './anchorNavigationConfig';

/** CSS var on :root the ADP mobile header reads to slide out of view on
 * scroll-down. Set here, reset on unmount. Decouples the anchor bar from the
 * header without querying the DOM by test-id. */
const HEADER_SHIFT_VAR = '--adp-header-shift';

const MOBILE_QUERY = '(max-width: 1023px)';

/** below this scrollY the header always shows (near top of page). */
const SHOW_HEADER_THRESHOLD = SCROLL_MARGIN_TOP.base;

/** ignore scroll-spy this long after a pill tap so smooth-scroll doesn't
 * flicker the highlight through intermediate clusters. */
const PROGRAMMATIC_SCROLL_MS = 800;

const setHeaderShift = (hidden: boolean) => {
  document.documentElement.style.setProperty(
    HEADER_SHIFT_VAR,
    hidden ? '-100%' : '0px',
  );
};

interface UseAnchorScrollBehaviorResult {
  /** cluster of the topmost section currently below the sticky bar */
  activeCluster: AnchorClusterKey | undefined;
  /** mobile-only: back-nav header slid away (scrolling down) */
  headerHidden: boolean;
  /** smooth-scroll to the first available section of a cluster + highlight it */
  scrollToCluster: (cluster: AnchorClusterKey) => void;
}

export const useAnchorScrollBehavior = (): UseAnchorScrollBehaviorResult => {
  const [activeCluster, setActiveCluster] = useState<
    AnchorClusterKey | undefined
  >(undefined);

  const [headerHidden, setHeaderHidden] = useState(false);

  const suppressSpyUntilRef = useRef(0);

  const scrollToCluster = useCallback((cluster: AnchorClusterKey) => {
    const targetId = CLUSTER_IDS[cluster]?.find((id) =>
      document.getElementById(id),
    );

    if (!targetId) {
      return;
    }

    suppressSpyUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_MS;

    setActiveCluster(cluster);

    document
      .getElementById(targetId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // --- scroll-spy: which cluster is in view -> active pill ---
  useEffect(() => {
    const elements = Object.values(ANCHOR_IDS)
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) {
      return;
    }

    const visible = new Set<HTMLElement>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.add(entry.target as HTMLElement);
          } else {
            visible.delete(entry.target as HTMLElement);
          }
        });

        if (Date.now() < suppressSpyUntilRef.current) {
          return;
        }

        const topmost = [...visible].sort(
          (a, b) =>
            a.getBoundingClientRect().top - b.getBoundingClientRect().top,
        )[0];

        if (topmost) {
          setActiveCluster(ID_TO_CLUSTER[topmost.id]);
        }
      },
      {
        // treat a section as active once its top clears the sticky header+bar
        rootMargin: `-${SCROLL_MARGIN_TOP.base}px 0px -60% 0px`,
        threshold: 0,
      },
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // --- scroll-direction (mobile only): hide/show the back-nav header ---
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !window.matchMedia(MOBILE_QUERY).matches
    ) {
      return;
    }

    let lastScrollY = window.scrollY;

    let ticking = false;

    const update = () => {
      const currentY = window.scrollY;

      if (currentY <= SHOW_HEADER_THRESHOLD) {
        setHeaderHidden(false);
      } else if (currentY > lastScrollY) {
        setHeaderHidden(true);
      } else if (currentY < lastScrollY) {
        setHeaderHidden(false);
      }

      lastScrollY = currentY;

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;

        window.requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);

      setHeaderShift(false);
    };
  }, []);

  // reflect header state to the CSS var the header slides on
  useEffect(() => {
    setHeaderShift(headerHidden);
  }, [headerHidden]);

  return { activeCluster, headerHidden, scrollToCluster };
};
